import React, { useEffect, useState } from 'react';
import { useCouponContext } from '../../../../contexts/CouponContextProvider';
import { CouponModal } from '@jbhi-fi/components/src/CouponModal';
import { Portal } from '../../../../components/portal/Portal';
import {
  BIRTHDAY_COUPON_REFERENCE,
  SIGNUP_COUPON_REFERENCE,
} from '../../../../contexts/CouponContextProvider/constants';
import { useCouponTracking } from '../../../../hooks/useCouponTracking/useCouponTracking';
import { generateAddToWalletQRCode } from '../../../../contexts/CouponContextProvider/utils';
import { useSubscriptionApi } from '../../customers/hooks/subscriptionApi';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag/useFeatureFlag';

type AddToWalletUrls = {
  appleLink?: string;
  googleLink?: string;
};

export const CouponsModalWrapper = () => {
  const [isCouponAnimationPlaying, setIsCouponAnimationPlaying] = useState(false);
  const {
    isCouponSurfacingEnabled,
    isLoading,
    isModalOpen,
    selectedCoupon,
    couponOffering,
    shouldShowSpendAndGetBanner,
    currentSpend,
    isProductExcluded,
    handleDeselectCoupon,
    handleApplyCoupon,
    handleRemoveCoupon,
    handleSelectCoupon,
    handleUpdateShowSpendAndGetBanner,
  } = useCouponContext({});
  const { getCouponCardAddToWalletLinks } = useSubscriptionApi();
  const [addToWalletUrls, setAddToWalletUrls] = useState<AddToWalletUrls>({});
  const [addToWalletErrorCode, setAddToWalletErrorCode] = useState<number>(0);
  const hasAddToWalletLinks = selectedCoupon?.digitalCard?.appleLink && selectedCoupon?.digitalCard?.googleLink;
  const isAddToWalletFeatureEnabled = useFeatureFlag('isAddToWalletsEnabled');

  useEffect(() => {
    setAddToWalletErrorCode(0);
    setAddToWalletUrls({});
  }, [selectedCoupon]);

  useEffect(() => {
    if (isAddToWalletFeatureEnabled) {
      const fetchAddToWalletUrls = async () => {
        if (hasAddToWalletLinks) {
          setAddToWalletUrls({
            appleLink: selectedCoupon?.digitalCard?.appleLink,
            googleLink: selectedCoupon?.digitalCard?.googleLink,
          });
        } else {
          if (selectedCoupon?.couponCode) {
            const { googleLink, appleLink, addToWalletErrorCode } = await getCouponCardAddToWalletLinks(
              selectedCoupon.couponCode
            );
            setAddToWalletUrls({ googleLink, appleLink });
            setAddToWalletErrorCode(addToWalletErrorCode ?? 0);
          }
        }
      };
      fetchAddToWalletUrls();
    }
  }, [selectedCoupon, hasAddToWalletLinks, addToWalletErrorCode]);

  const offering = couponOffering
    ? {
        coupon: couponOffering,
        currentSpend,
        shouldShowSpendAndGetBanner,
        isProductExcluded,
        handleSelectCoupon,
        handleUpdateShowSpendAndGetBanner,
      }
    : undefined;

  const { trackCouponEvent } = useCouponTracking(selectedCoupon);
  const handleOnClose = () => {
    handleDeselectCoupon();
    trackCouponEvent({ event: 'close_popup' });
    setIsCouponAnimationPlaying(false);
  };
  const handleOnRemoveCoupon = async (couponCode: string) => {
    await handleRemoveCoupon(couponCode);
    setTimeout(() => {
      handleDeselectCoupon();
    }, 5000);
    setIsCouponAnimationPlaying(false);
  };
  const handleApplyCouponAnimation = async () => {
    await handleApplyCoupon();
    const couponName = selectedCoupon?.couponName?.toUpperCase();
    const isPerksBirthdayOrWelcomeCoupon =
      couponName?.includes(BIRTHDAY_COUPON_REFERENCE) || couponName?.includes(SIGNUP_COUPON_REFERENCE);
    if (isPerksBirthdayOrWelcomeCoupon) {
      setIsCouponAnimationPlaying(true);
    } else {
      setTimeout(() => {
        handleDeselectCoupon();
      }, 5000);
      setIsCouponAnimationPlaying(false);
    }
  };
  if (!isCouponSurfacingEnabled) return null;
  const container = document.body;

  return isModalOpen && selectedCoupon ? (
    <Portal container={container}>
      <CouponModal
        isOpen={isModalOpen}
        coupon={selectedCoupon}
        couponOffering={offering}
        onApplyCoupon={handleApplyCouponAnimation}
        onRemoveCoupon={handleOnRemoveCoupon}
        onClose={handleOnClose}
        isLoading={isLoading}
        playCouponAppliedAnimation={isCouponAnimationPlaying}
        generateAddToWalletQRCode={generateAddToWalletQRCode}
        addToWalletUrls={addToWalletUrls}
        addToWalletErrorCode={addToWalletErrorCode}
        shouldShowAddToWalletButtons={!!isAddToWalletFeatureEnabled}
      />
    </Portal>
  ) : null;
};
